import { FormControlMixin, LabelMixin } from '@umbraco-ui/uui-base/lib/mixins';
import { LitElement, html, css } from 'lit';
import { property, query } from 'lit/decorators.js';
import { UUIEvent } from '@umbraco-ui/uui-base/lib/events';

var __defProp$1 = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
class UUIBooleanInputEvent extends UUIEvent {
  constructor(evName, eventInit = {}) {
    super(evName, __spreadValues(__spreadValues({}, { bubbles: true }), eventInit));
  }
}
UUIBooleanInputEvent.CHANGE = "change";

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
class UUIBooleanInputElement extends FormControlMixin(
  LabelMixin("", LitElement)
) {
  constructor(inputRole = "checkbox") {
    super();
    this.labelPosition = "right";
    this._checked = false;
    this.disabled = false;
    if (this._value === "") {
      this._value = "on";
    }
    this.inputRole = inputRole;
    this.addEventListener("keypress", this._onKeypress);
  }
  /** intentional overwrite of FormControlMixins value getter and setter method. */
  get value() {
    return this._value;
  }
  set value(newVal) {
    const oldValue = this._value;
    this._value = newVal;
    if ("ElementInternals" in window && //@ts-ignore
    "setFormValue" in window.ElementInternals.prototype) {
      this._internals.setFormValue(
        this._checked && this.name !== "" ? this._value : null
      );
    }
    this.requestUpdate("value", oldValue);
  }
  // Do not 'reflect' as the attribute is used as fallback.
  get checked() {
    return this._checked;
  }
  set checked(newVal) {
    const oldValue = this._checked;
    this._checked = newVal;
    this._internals.setFormValue(
      this._checked && this.name !== "" ? this._value ? this._value : "on" : null
    );
    this.requestUpdate("checked", oldValue);
  }
  getFormElement() {
    return this._input;
  }
  _onKeypress(e) {
    if (e.key == "Enter") {
      this.submit();
    }
  }
  hasValue() {
    return this.checked;
  }
  formResetCallback() {
    super.formResetCallback();
    this.checked = this.hasAttribute("checked");
  }
  firstUpdated(_changedProperties) {
    var _a;
    super.firstUpdated(_changedProperties);
    const labelEl = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector("label");
    let hadMouseDown = false;
    this._input.addEventListener("blur", () => {
      if (hadMouseDown === false) {
        this.style.setProperty("--uui-show-focus-outline", "1");
      }
      hadMouseDown = false;
    });
    labelEl.addEventListener("mousedown", () => {
      this.style.setProperty("--uui-show-focus-outline", "0");
      hadMouseDown = true;
    });
    labelEl.addEventListener("mouseup", () => {
      hadMouseDown = false;
    });
  }
  /**
   * This method enables <label for="..."> to focus the input
   */
  focus() {
    this._input.focus();
  }
  click() {
    this._input.click();
  }
  _onInputChange(e) {
    e.stopPropagation();
    this.pristine = false;
    this.checked = this._input.checked;
    this.dispatchEvent(new UUIBooleanInputEvent(UUIBooleanInputEvent.CHANGE));
  }
  render() {
    return html`
      <label>
        <input
          id="input"
          type="checkbox"
          @change="${this._onInputChange}"
          .disabled=${this.disabled}
          .checked=${this.checked}
          aria-checked="${this.checked ? "true" : "false"}"
          aria-label=${this.label}
          role="${this.inputRole}" />
        ${this.renderCheckbox()} ${this.renderLabel()}
      </label>
    `;
  }
}
UUIBooleanInputElement.styles = [
  css`
      :host {
        display: inline-block;
      }

      label {
        cursor: pointer;
        user-select: none;

        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        justify-items: center;
        gap: var(--uui-size-3,9px);
      }

      input {
        position: absolute;
        height: 0px;
        width: 0px;
        margin-top: -4px;
      }

      :host([label-position='left']) label {
        flex-direction: row-reverse;
      }

      :host([label-position='top']) label {
        gap: var(--uui-size-half-base-unit);
        flex-direction: column-reverse;
      }

      :host([label-position='bottom']) label {
        gap: var(--uui-size-half-base-unit);
        flex-direction: column;
      }

      :host([disabled]) .label {
        opacity: 0.5;
      }
    `
];
__decorateClass([
  property({ type: String, attribute: "label-position", reflect: true })
], UUIBooleanInputElement.prototype, "labelPosition", 2);
__decorateClass([
  property({ type: Boolean })
], UUIBooleanInputElement.prototype, "checked", 1);
__decorateClass([
  property({ type: Boolean, reflect: true })
], UUIBooleanInputElement.prototype, "disabled", 2);
__decorateClass([
  query("#input")
], UUIBooleanInputElement.prototype, "_input", 2);

export { UUIBooleanInputElement, UUIBooleanInputEvent };
